import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, BedDouble, Maximize, MapPin, Check, Phone } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";

export default function ApartmentPage() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [apt, setApt] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setActiveImg(0);
    api.get(`/apartments/${slug}`).then((r) => setApt(r.data)).catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) {
    return (
      <main className="min-h-screen flex items-center justify-center" data-testid="apartment-not-found">
        <p className="font-serif text-3xl">{t("notFound")}</p>
      </main>
    );
  }

  if (!apt) {
    return <main className="min-h-screen bg-sand" data-testid="apartment-loading" />;
  }

  const name = lang === "mk" ? apt.name_mk : apt.name_en;
  const inputCls =
    "w-full bg-transparent border border-line/80 px-4 py-3 text-sm placeholder:text-ink/35 focus:outline-none focus:border-terra transition-colors duration-300";

  if (apt.coming_soon) {
    return (
      <main data-testid="apartment-coming-soon-page" className="pt-20 md:pt-24 pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Link to="/" data-testid="back-to-apartments" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ink/50 hover:text-terra transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {t("detail.back")}
          </Link>
          {apt.images.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center px-6 min-h-[70vh]">
              <p className="text-terra text-xs font-bold tracking-[0.35em] uppercase mb-4">{lang === "mk" ? apt.category_mk : apt.category_en}</p>
              <h1 className="font-serif text-4xl md:text-6xl tracking-tight mb-8">{name}</h1>
              <span className="font-serif italic text-6xl md:text-8xl text-terra/60 leading-none" data-testid="coming-soon-title">{t("grid.comingSoon")}</span>
              <p className="text-ink/55 text-sm md:text-base mt-10 max-w-md leading-relaxed">{t("grid.comingSoonDetail")}</p>
            </div>
          ) : (
            <>
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                  <div>
                    <p className="text-terra text-xs font-bold tracking-[0.35em] uppercase mb-3 inline-flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> {lang === "mk" ? apt.location_mk : apt.location_en}
                    </p>
                    <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">{name}</h1>
                  </div>
                  <p className="font-serif italic text-3xl md:text-4xl text-terra whitespace-nowrap" data-testid="coming-soon-title">{t("grid.comingSoon")}</p>
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, scale: 0.985 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }} className="mb-10">
                <div className="spotlight-frame aspect-[16/10] md:aspect-[16/8] border border-line/70 mb-3">
                  <img src={apt.images[activeImg]} alt={name} data-testid="apartment-main-image" className="w-full h-full object-cover" />
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: "thin" }}>
                  {apt.images.map((img, i) => (
                    <button
                      key={i}
                      data-testid={`apartment-thumb-${i}`}
                      onClick={() => setActiveImg(i)}
                      className={`spotlight-frame shrink-0 w-32 md:w-44 aspect-[16/10] border transition-colors duration-300 ${activeImg === i ? "border-terra" : "border-line/60 opacity-70 hover:opacity-100"}`}
                    >
                      <img src={img} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              </motion.div>
              <p className="text-ink/55 text-sm md:text-base max-w-md leading-relaxed">{t("grid.comingSoonDetail")}</p>
            </>
          )}
        </div>
      </main>
    );
  }

  return (
    <main data-testid="apartment-page" className="pt-20 md:pt-24 pb-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Link to="/" data-testid="back-to-apartments" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ink/50 hover:text-terra transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {t("detail.back")}
        </Link>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <p className="text-terra text-xs font-bold tracking-[0.35em] uppercase mb-3 inline-flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> {lang === "mk" ? apt.location_mk : apt.location_en}
              </p>
              <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">{name}</h1>
            </div>
            {apt.coming_soon ? (
              <p className="font-serif italic text-3xl md:text-4xl text-terra whitespace-nowrap" data-testid="coming-soon-title">{t("grid.comingSoon")}</p>
            ) : (
              <p className="font-serif text-3xl md:text-4xl whitespace-nowrap">
                €{apt.price_per_night}
                <span className="font-sans text-sm text-ink/50"> {t("grid.perNight")}</span>
              </p>
            )}
          </div>
          <p className="text-ink/60 text-base md:text-lg font-serif italic mb-10">{lang === "mk" ? apt.tagline_mk : apt.tagline_en}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.985 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          className="mb-14"
        >
          <div className="spotlight-frame aspect-[16/10] md:aspect-[16/8] border border-line/70 mb-3">
            <img src={apt.images[activeImg]} alt={name} data-testid="apartment-main-image" className="w-full h-full object-cover" />
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: "thin" }}>
            {apt.images.map((img, i) => (
              <button
                key={i}
                data-testid={`apartment-thumb-${i}`}
                onClick={() => setActiveImg(i)}
                className={`spotlight-frame shrink-0 w-32 md:w-44 aspect-[16/10] border transition-colors duration-300 ${activeImg === i ? "border-terra" : "border-line/60 opacity-70 hover:opacity-100"}`}
              >
                <img src={img} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="flex flex-wrap gap-6 text-sm text-ink/60 pb-8 mb-8 border-b border-line/70">
              <span className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-terra" strokeWidth={1.5} /> {apt.max_guests} {t("grid.guests")}</span>
              <span className="inline-flex items-center gap-2"><BedDouble className="w-4 h-4 text-terra" strokeWidth={1.5} /> {apt.bedrooms === 0 ? t("grid.studio") : `${apt.bedrooms} ${t("grid.bedrooms")}`}</span>
              <span className="inline-flex items-center gap-2"><Maximize className="w-4 h-4 text-terra" strokeWidth={1.5} /> {apt.size_m2} m²</span>
            </div>

            {(lang === "mk" ? apt.beds_mk : apt.beds_en) && (
              <div className="flex items-start gap-4 border border-terra/40 bg-terra/5 px-5 py-4 mb-12 text-sm leading-relaxed" data-testid="beds-info">
                <BedDouble className="w-5 h-5 text-terra shrink-0 mt-0.5" strokeWidth={1.5} />
                <span>{lang === "mk" ? apt.beds_mk : apt.beds_en}</span>
              </div>
            )}

            <h2 className="font-serif text-3xl tracking-tight mb-4">{t("detail.about")}</h2>
            <p className="text-ink/65 leading-relaxed text-sm md:text-base mb-12 max-w-2xl">
              {lang === "mk" ? apt.description_mk : apt.description_en}
            </p>

            <h2 className="font-serif text-3xl tracking-tight mb-6">{t("detail.amenities")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-14">
              {apt.amenities.map((a) => (
                <div key={a} className="flex items-center gap-2.5 border border-line/70 px-4 py-3 text-sm" data-testid={`amenity-${a}`}>
                  <Check className="w-4 h-4 text-terra shrink-0" strokeWidth={2} />
                  {t(`detail.amenityList.${a}`)}
                </div>
              ))}
            </div>

          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="lg:col-span-5"
          >
            {!apt.coming_soon && (
            <div data-testid="contact-booking-card" className="lg:sticky lg:top-28 border border-ink/10 bg-coal text-sand p-8 md:p-10">
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-4">{t("detail.bookVia")}</h3>
              <p className="text-sand/60 text-sm leading-relaxed mb-8">{t("detail.bookViaSub")}</p>
              <div className="space-y-3 mb-8">
                <a href={`https://wa.me/38978321872?text=${encodeURIComponent(t("detail.waMsg").replace("{name}", name))}`} target="_blank" rel="noopener noreferrer" data-testid="book-whatsapp-button" className="flex items-center justify-center gap-3 w-full px-6 py-4 text-xs font-bold tracking-[0.2em] uppercase transition-transform duration-300 hover:scale-[1.02]" style={{ backgroundColor: "#25D366", color: "#0b1f13" }}>
                  <img src="https://cdn.simpleicons.org/whatsapp/0b1f13" alt="WhatsApp" className="w-5 h-5" /> WhatsApp
                </a>
                <a href="viber://chat?number=%2B38978321872" data-testid="book-viber-button" className="flex items-center justify-center gap-3 w-full px-6 py-4 text-xs font-bold tracking-[0.2em] uppercase text-white transition-transform duration-300 hover:scale-[1.02]" style={{ backgroundColor: "#7360F2" }}>
                  <img src="https://cdn.simpleicons.org/viber/ffffff" alt="Viber" className="w-5 h-5" /> Viber
                </a>
              </div>
              <p className="text-[10px] uppercase tracking-widest text-sand/45 mb-2">{t("detail.orCall")}</p>
              <a href="tel:+38978321872" data-testid="book-phone-button" className="inline-flex items-center gap-2 font-serif text-2xl text-ember hover:text-sand transition-colors">
                <Phone className="w-5 h-5" strokeWidth={1.5} /> +389 78 321 872
              </a>
            </div>
            )}

            {apt.coming_soon && (
              <div data-testid="coming-soon-panel" className="lg:sticky lg:top-28 border border-ink/10 bg-coal text-sand p-8 md:p-10 text-center">
                <p className="text-ember text-xs font-bold tracking-[0.35em] uppercase mb-4">{t("grid.comingSoon")}</p>
                <h3 className="font-serif text-3xl tracking-tight mb-4">{name}</h3>
                <p className="text-sand/65 text-sm leading-relaxed">{t("grid.comingSoonDetail")}</p>
              </div>
            )}
          </motion.aside>
        </div>
      </div>
    </main>
  );
}
